import { CheckCircle2, ArrowRight, Search, Bell } from "lucide-react";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Dos tarjetas visibles en el home: Gratis (escáner + alertas) y Pro (pago único $14.990). */
export default function PricingCards({ onScrollToScanner }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const goPagar = async () => {
    setLoading(true);
    trackEvent("home_pricing_cta_click", { plan: "pro_pago_unico" });
    try {
      // Verificar sesión: si no hay, mandamos a /registro?checkout=transbank
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!r?.data?.id) throw new Error("no-session");
      // Con sesión → iniciar Transbank
      const { data } = await axios.post(`${API}/pro/checkout/transbank`, {}, { withCredentials: true });
      if (data?.buy_order === "ALREADY_PRO") {
        toast.success("Ya tienes acceso Pro. Entra a tu cuenta.");
        navigate("/cuenta");
        return;
      }
      if (data?.url && data?.token) {
        redirectToWebpay(data.url, data.token);
        return;
      }
      toast.error("Webpay no devolvió el link. Reintenta.");
    } catch (err) {
      // Sin sesión → registro con intención de checkout
      if (err?.message === "no-session" || err?.response?.status === 401) {
        navigate("/registro?checkout=transbank");
        return;
      }
      const detail = err?.response?.data?.detail || "No pudimos iniciar el pago. Intenta de nuevo.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pricing" data-testid="home-pricing" className="px-6 py-16 sm:py-24 bg-white border-t border-slate-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
            <span className="inline-block w-6 h-px bg-blue-400/60" />
            Precios simples
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
            Un solo pago. Todo incluido.
          </h2>
          <p className="mt-3 text-slate-600 text-base">
            Sin suscripciones, sin letra chica. Escáner gratis para siempre. Documentos y alertas por un pago único.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* GRATIS */}
          <div data-testid="pricing-card-free" className="rounded-2xl border border-slate-200 bg-white p-8 flex flex-col">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-widest font-semibold text-slate-500 uppercase">
              <Search className="w-3.5 h-3.5" /> Gratis
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-5xl font-bold text-ink-900">$0</span>
              <span className="text-slate-500 text-sm">CLP</span>
            </div>
            <p className="mt-2 text-slate-600 text-sm">Sin registro. Sin tarjeta.</p>

            <ul className="mt-6 space-y-3 text-sm text-slate-700 flex-1">
              <Feature>Escaneo de tu sitio (3 por mes)</Feature>
              <Feature>Reporte con score + hallazgos priorizados</Feature>
              <Feature>Simulador de multas</Feature>
            </ul>

            <button
              data-testid="pricing-cta-free"
              onClick={onScrollToScanner}
              className="mt-8 h-12 px-5 rounded-xl border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold inline-flex items-center justify-center gap-2 transition"
            >
              Escanear mi sitio
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* PRO */}
          <div
            data-testid="pricing-card-pro"
            className="relative rounded-2xl border-2 border-[#1E3A8A] bg-gradient-to-br from-[#0f1f4a] to-[#1E3A8A] text-white p-8 flex flex-col shadow-xl shadow-blue-900/20"
          >
            <div className="absolute -top-3 left-8">
              <span className="inline-block px-3 py-1 rounded-full bg-amber-300 text-slate-900 text-[10px] font-bold tracking-widest uppercase">
                Más elegido
              </span>
            </div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-widest font-semibold text-blue-200 uppercase">
              <Bell className="w-3.5 h-3.5" /> Pro · Pago único
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-5xl font-bold">$14.990</span>
              <span className="text-blue-200 text-sm">CLP · una vez</span>
            </div>
            <p className="mt-2 text-blue-100 text-sm">
              Acceso permanente. Sin mensualidades, sin cobros futuros.
            </p>

            <ul className="mt-6 space-y-3 text-sm text-blue-50 flex-1">
              <Feature light>Escaneos <strong className="text-white">ilimitados</strong> de cualquier sitio</Feature>
              <Feature light>Los <strong className="text-white">7 documentos legales</strong> de la Ley 21.719 generados con IA</Feature>
              <Feature light>Descarga en PDF · Markdown · TXT</Feature>
              <Feature light>Guías por plataforma: WordPress, Shopify, Wix, Jumpseller, Squarespace</Feature>
              <Feature light>Alertas de la ley gratis de por vida</Feature>
              <Feature light>Onboarding personalizado con tu razón social + RUT</Feature>
            </ul>

            <button
              data-testid="pricing-cta-pro"
              onClick={goPagar}
              disabled={loading}
              className="mt-8 h-12 px-6 rounded-xl bg-white text-[#1E3A8A] hover:bg-slate-100 active:scale-[0.98] disabled:opacity-60 font-semibold inline-flex items-center justify-center gap-2 transition-all"
            >
              {loading ? "Iniciando pago…" : (<>Desbloquear todo — $14.990 <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <p className="mt-3 text-[11px] text-blue-200 text-center">
              Pago único · Webpay · sin renovación
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ children, light }) {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${light ? "text-emerald-300" : "text-emerald-600"}`} />
      <span>{children}</span>
    </li>
  );
}

/** Redirige a Webpay haciendo un POST-form con token_ws (obligatorio por doc Transbank). */
function redirectToWebpay(url, token) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "token_ws";
  input.value = token;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

export { redirectToWebpay };
